import * as React from 'react';
import propz from 'propz';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../../consts/report';
import {
  ReportActions,
  ReportDateRange,
  ReportDescription,
  ReportRadioGroup,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/Reports/ReportFormUi';
import '../../Clubs.css';

const GROUP_BY = {
  SCHOOL_FORMS: 'isGroupByForms',
  YEAR_GROUPS: 'isGroupByYears',
  KEY_STAGES: 'isGroupByKeyStages'
};

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  activeSchoolId: string;
  school: any;
}

const getAcademicYear = (date: Date) => (date.getMonth() >= 8 ? date.getFullYear() : date.getFullYear() - 1);

// report 001
export const AggregateClubParticipationReportForm = ({ onCloseClick, onSubmit, activeSchoolId, school }: Props) => {
  const [schoolData, setSchoolData] = React.useState<any>(null);

  React.useEffect(() => {
    fetch('/dist/images/mis/misIntegrationSchoolsList.json')
      .then(response => response.json())
      .then(data => setSchoolData(data.find(item => item.schoolSITId === activeSchoolId)))
      .catch(error => console.error('Error loading JSON:', error));
  }, [activeSchoolId]);

  const today = new Date();
  const startOfToday = new Date(today);
  startOfToday.setHours(0, 0, 1, 0);
  const endOfToday = new Date(today);
  endOfToday.setHours(23, 59, 59, 999);
  const initialValues = {
    dateFrom: startOfToday,
    dateTo: endOfToday,
    isPrintPP: false,
    isPrintSEN: false,
    isPrintFSM: false,
    isPrintEthnicity: false,
    groupBy: GROUP_BY.SCHOOL_FORMS
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialValues,
        isPrintPP: schoolData ? schoolData.studentsPPInfo === 1 : false,
        isPrintSEN: schoolData ? schoolData.studentsSENInfo === 1 : false,
        isPrintFSM: schoolData ? schoolData.studentsFSMInfo === 1 : false,
        isPrintEthnicity: schoolData ? schoolData.studentsEthnicityCode === 1 : false
      }}
      onSubmit={values => {
        const valuesToTransfer: any = { ...values };
        propz.set(valuesToTransfer, [values.groupBy], true);
        delete valuesToTransfer.groupBy;
        onSubmit(valuesToTransfer, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.AGGREGATE_CLUB_PARTICIPATION_FOR_PERIOD);
      }}
    >
      {({ values, setFieldValue }) => {
        const reportAcademicYear = getAcademicYear(values.dateFrom);
        const isCrossAcademicYear = reportAcademicYear !== getAcademicYear(values.dateTo);
        const isHistoricalPeriod = reportAcademicYear !== getAcademicYear(new Date());
        const isEnglishYearGroups = propz.get(school, ['ageGroupsNaming'], 'ENGLISH') === 'ENGLISH';
        const formGroupingUnavailable = isHistoricalPeriod;
        const keyStageGroupingUnavailable = !isEnglishYearGroups;
        const hasInvalidGrouping =
          (values.groupBy === GROUP_BY.SCHOOL_FORMS && formGroupingUnavailable) ||
          (values.groupBy === GROUP_BY.KEY_STAGES && keyStageGroupingUnavailable);
        const validationMessage = isCrossAcademicYear
          ? 'Choose dates within one academic year (1 September to 31 August).'
          : hasInvalidGrouping
          ? values.groupBy === GROUP_BY.SCHOOL_FORMS
            ? 'Registration-form history is not stored. Use year groups or key stages for past academic years.'
            : 'Key stages are only available to schools using English year groups.'
          : isHistoricalPeriod &&
            (values.isPrintPP || values.isPrintSEN || values.isPrintFSM || values.isPrintEthnicity)
          ? 'Student breakdowns use the values currently recorded on each student profile.'
          : 'Ready to generate';

        const setDateRangeValue = (field: 'dateFrom' | 'dateTo', date: Date) => {
          setFieldValue(field, date);
          const nextFrom = field === 'dateFrom' ? date : values.dateFrom;
          if (values.groupBy === GROUP_BY.SCHOOL_FORMS && getAcademicYear(nextFrom) !== getAcademicYear(new Date())) {
            setFieldValue('groupBy', GROUP_BY.YEAR_GROUPS);
          }
        };

        return (
          <Form className="eReportForm">
            <div className="eReportForm__scroll">
              <ReportDescription>
                Count each student once if they were listed as a participant in at least one club session during the
                selected period, grouped by registration form, year group or key stage.
              </ReportDescription>

              <ReportSection
                number={1}
                title="Reporting period"
                hint="Choose the date range covered by the report."
                required
              >
                <ReportDateRange
                  dateFrom={values.dateFrom}
                  dateTo={values.dateTo}
                  onChangeFrom={date => {
                    setDateRangeValue('dateFrom', date);
                    if (values.dateTo < date) setFieldValue('dateTo', date);
                  }}
                  onChangeTo={date => setDateRangeValue('dateTo', date)}
                />
              </ReportSection>

              <ReportSection number={2} title="Group results" hint="Choose how participation totals are organised.">
                <ReportRadioGroup
                  name="groupBy"
                  value={values.groupBy}
                  onChange={value => setFieldValue('groupBy', value)}
                  options={[
                    {
                      value: GROUP_BY.SCHOOL_FORMS,
                      label: 'Registration forms',
                      hint: formGroupingUnavailable
                        ? 'Only available for the current academic year.'
                        : 'Group students by registration form.',
                      disabled: formGroupingUnavailable
                    },
                    { value: GROUP_BY.YEAR_GROUPS, label: 'Year groups', hint: 'Group students by school year.' },
                    {
                      value: GROUP_BY.KEY_STAGES,
                      label: 'Key stages',
                      hint: keyStageGroupingUnavailable
                        ? 'Only available to schools using English year groups.'
                        : 'Group students by key stage.',
                      disabled: keyStageGroupingUnavailable
                    }
                  ]}
                />
              </ReportSection>

              <ReportSection
                number={3}
                title="Student breakdowns"
                hint="Choose the additional breakdowns included in the totals."
              >
                <ReportToggleList>
                  <ReportToggle
                    name="isPrintPP"
                    label="Pupil premium"
                    hint="Show how many participating students are recorded as PP."
                    value={values.isPrintPP}
                    onChange={value => setFieldValue('isPrintPP', value)}
                  />
                  <ReportToggle
                    name="isPrintSEN"
                    label="Special educational needs"
                    hint="Show how many participating students are recorded as SEN."
                    value={values.isPrintSEN}
                    onChange={value => setFieldValue('isPrintSEN', value)}
                  />
                  <ReportToggle
                    name="isPrintFSM"
                    label="Free school meals"
                    hint="Show how many participating students are recorded as FSM."
                    value={values.isPrintFSM}
                    onChange={value => setFieldValue('isPrintFSM', value)}
                  />
                  <ReportToggle
                    name="isPrintEthnicity"
                    label="Ethnicity"
                    hint="Add a separate total for each recorded ethnicity code."
                    value={values.isPrintEthnicity}
                    onChange={value => setFieldValue('isPrintEthnicity', value)}
                  />
                </ReportToggleList>
              </ReportSection>
            </div>
            <ReportActions
              onCancel={onCloseClick}
              disabled={isCrossAcademicYear || hasInvalidGrouping}
              status={validationMessage}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
